import { 
  ColumnDef, 
  ColumnFiltersState, 
  flexRender, 
  getCoreRowModel, 
  getFilteredRowModel, 
  getPaginationRowModel, 
  getSortedRowModel, 
  PaginationState, 
  RowData, 
  SortingState, 
  useReactTable 
} from "@tanstack/react-table";

import React, { useCallback, useEffect, useMemo, useState } from "react";
import EditableCell from "./EditableCell";
import { Spinner, Row, Col, Table, Pagination, Form } from "react-bootstrap";

// Extend ColumnMeta for @tanstack/react-table (for custom meta properties)
declare module '@tanstack/react-table' {
  interface ColumnMeta<TData extends RowData, TValue> {
    headerClass: string;
    cellClass: string;
    isEditable?: boolean; // Add this flag to tell DataTable to use EditableCell
  }
}

// Helper to prevent pagination reset on data updates (from original code)
function useSkipper() {
  const shouldSkipRef = React.useRef(true);
  const shouldSkip = shouldSkipRef.current;

  const skip = React.useCallback(() => {
    shouldSkipRef.current = false;
  }, []);

  React.useEffect(() => {
    shouldSkipRef.current = true;
  });

  return [shouldSkip, skip] as const;
}

interface DataTableProps<TData extends RowData> {
  data: TData[];
  columns: ColumnDef<TData, any>[];
  initialColumnFilters?: ColumnFiltersState;
  isLoading?: boolean;
  enablePagination?: boolean;
  enableSorting?: boolean;
  enableGlobalFilter?: boolean;
  globalFilterPlaceholder?: string;
  onRowDataChange?: (rowId: string, columnId: string, value: any) => void;
  defaultPageSize?: number;
  pageSizes?: number[];
  className?: string;
  initialSortBy?: { id: string; desc?: boolean };
  /**
   * Remember the page (and page size) under this key for the rest of the
   * browser session, so leaving the table and coming back doesn't drop the
   * user on page 1. Keys have to be unique per table instance.
   */
  persistKey?: string;
  /**
   * Scroll to this row once the data is in and flag it briefly, so someone
   * returning from a detail page can see which row they came from. Needs
   * getRowKey to know which row is which.
   */
  focusRowId?: string | null;
  getRowKey?: (row: TData) => string;
  /**
   * Called when a row is clicked, with every row the user can currently see
   * (sorted and filtered, across pages) so the caller can step through them.
   * Clicks that land on a control inside a cell (the action column, an
   * editable cell) are ignored.
   */
  onRowClick?: (row: TData, visibleRows: TData[]) => void;
}

const readStoredPagination = (
  persistKey: string | undefined,
  defaultPageSize: number
): PaginationState => {
  const fallback = { pageIndex: 0, pageSize: defaultPageSize };
  if (!persistKey) return fallback;

  try {
    const stored = sessionStorage.getItem(`datatable:${persistKey}`);
    if (!stored) return fallback;

    const parsed = JSON.parse(stored);
    return {
      pageIndex: Number.isInteger(parsed?.pageIndex) ? parsed.pageIndex : 0,
      pageSize: Number.isInteger(parsed?.pageSize) ? parsed.pageSize : defaultPageSize,
    };
  } catch {
    // Private browsing, cleared storage, or a stale shape - start clean.
    return fallback;
  }
};

function DataTable<TData extends RowData>({
  data,
  columns,
  initialColumnFilters = [],
  isLoading = false,
  enablePagination = true,
  enableSorting = true,
  enableGlobalFilter = true,
  globalFilterPlaceholder = 'Search all columns...',
  onRowDataChange,
  defaultPageSize = 10,
  pageSizes = [10, 20, 50, 100],
  className = '',
  initialSortBy,
  persistKey,
  focusRowId,
  getRowKey,
  onRowClick,
}: DataTableProps<TData>) {
  const [sorting, setSorting] = useState<SortingState>(
    initialSortBy
      ? [{ id: initialSortBy.id, desc: !!initialSortBy.desc }]
      : []
  );
  const [globalFilter, setGlobalFilter] = useState<string>('');
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>(initialColumnFilters);
  const [pagination, setPagination] = useState<PaginationState>(() =>
    readStoredPagination(persistKey, defaultPageSize)
  );
  const [autoResetPageIndex, skipAutoResetPageIndex] = useSkipper();

  useEffect(() => {
    if (!persistKey) return;

    try {
      sessionStorage.setItem(`datatable:${persistKey}`, JSON.stringify(pagination));
    } catch {
      // Storage unavailable - the table still works, it just won't remember.
    }
  }, [persistKey, pagination]);

  // Ensure data and columns are always treated as arrays
  const safeData = useMemo(() => Array.isArray(data) ? data : [], [data]);
  const safeColumns = useMemo(() => Array.isArray(columns) ? columns : [], [columns]);

  const memoizedColumns = useMemo(() => {
    if (onRowDataChange) {
      return safeColumns.map(col => {
        if ((col.meta as any)?.isEditable) {
          return {
            ...col,
            cell: (cellInfo: any) => (
              <EditableCell
                getValue={cellInfo.getValue}
                row={{ id: cellInfo.row.id, original: cellInfo.row.original }}
                column={{ id: cellInfo.column.id }}
                onUpdate={(rowId, colId, value) => {
                  skipAutoResetPageIndex();
                  onRowDataChange(rowId, colId, value);
                }}
              />
            ),
          };
        }
        return col;
      });
    }
    return safeColumns;
  }, [safeColumns, onRowDataChange, skipAutoResetPageIndex]);

  useEffect(() => {
    setColumnFilters(prev => {
      if (JSON.stringify(prev) !== JSON.stringify(initialColumnFilters)) {
        return [
          ...prev.filter(f => !initialColumnFilters.some(nf => nf.id === f.id)),
          ...initialColumnFilters
        ];
      }
      return prev;
    });  
  }, [initialColumnFilters]);

  const table = useReactTable({
    data: safeData, // Use safeData here
    columns: memoizedColumns, // Use memoizedColumns here
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: enableSorting ? getSortedRowModel() : undefined,
    getFilteredRowModel: enableGlobalFilter ? getFilteredRowModel() : undefined,
    getPaginationRowModel: enablePagination ? getPaginationRowModel() : undefined, // Corrected line
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
    onPaginationChange: setPagination,
    onColumnFiltersChange: setColumnFilters,
    state: {
      columnFilters,
      globalFilter,
      sorting,
      pagination,
    },
    // Data arrives after the first render, and the default auto-reset would
    // throw away a page restored from storage.
    autoResetPageIndex: persistKey ? false : autoResetPageIndex,
    autoResetExpanded: false,
    debugTable: false,
  });

  const containerRef = React.useRef<HTMLDivElement>(null);
  const [flaggedRowId, setFlaggedRowId] = useState<string | null>(null);

  const rows = table.getRowModel().rows;
  const rowCount = rows.length;
  const focusAppliedRef = React.useRef<string | null>(null);
  const focusTimersRef = React.useRef<ReturnType<typeof setTimeout>[]>([]);
  const getRowKeyRef = React.useRef(getRowKey);
  getRowKeyRef.current = getRowKey;

  const stickUntilRef = React.useRef(0);

  useEffect(
    () => () => {
      focusTimersRef.current.forEach(clearTimeout);
    },
    []
  );

  const { pageIndex, pageSize } = pagination;
  // Layout effect, not effect: this has to run before the browser paints, or
  // the user sees the top of the page for a frame and then a jump down.
  React.useLayoutEffect(() => {
    const keyOf = getRowKeyRef.current;
    if (!focusRowId || !keyOf || rowCount === 0) return;
    if (focusAppliedRef.current === focusRowId) return;

    const allRows = table.getPrePaginationRowModel().rows;
    const rowIndex = allRows.findIndex((row) => keyOf(row.original) === focusRowId);

    if (rowIndex === -1) {
      // Deleted, or filtered out by an active search - land on the table
      // rather than leaving the browser at the top of the page.
      focusAppliedRef.current = focusRowId;
      containerRef.current?.scrollIntoView({ block: 'start' });
      return;
    }

    // A newly added article can sit on a different page than the one the user
    // left, so go to its page first and let this run again.
    const targetPage = enablePagination ? Math.floor(rowIndex / pageSize) : 0;
    if (enablePagination && targetPage !== pageIndex) {
      setPagination((prev) => ({ ...prev, pageIndex: targetPage }));
      return;
    }

    focusAppliedRef.current = focusRowId;
    setFlaggedRowId(focusRowId);

    containerRef.current
      ?.querySelector(`tr[data-row-key="${CSS.escape(focusRowId)}"]`)
      ?.scrollIntoView({ block: 'center' });

    // The rest of the page is still arriving (other requests, images), and
    // anything that grows above the table would drag the row off-screen.
    // Re-centre for a moment so it stays put instead of drifting.
    stickUntilRef.current = Date.now() + 1200;
    focusTimersRef.current = [setTimeout(() => setFlaggedRowId(null), 3000)];
  }, [focusRowId, rowCount, pageIndex, pageSize, enablePagination, table]);

  // Deliberately runs on every render while the window is open, before paint,
  // so the row appears to hold still rather than jumping.
  React.useLayoutEffect(() => {
    if (!flaggedRowId || Date.now() > stickUntilRef.current) return;

    containerRef.current
      ?.querySelector(`tr[data-row-key="${CSS.escape(flaggedRowId)}"]`)
      ?.scrollIntoView({ block: 'center' });
  });

  const pageCount = table.getPageCount();
  useEffect(() => {
    if (!enablePagination || pageCount === 0) return;

    setPagination((prev) =>
      prev.pageIndex > pageCount - 1 ? { ...prev, pageIndex: pageCount - 1 } : prev
    );
  }, [enablePagination, pageCount]);

  const paginationRange = useCallback(() => {
    const currentPage = table.getState().pagination.pageIndex + 1;
    const totalPages = table.getPageCount();
    const maxPagesToShow = 5;
    const halfRange = Math.floor(maxPagesToShow / 2);

    let startPage = Math.max(1, currentPage - halfRange);
    let endPage = Math.min(totalPages, currentPage + halfRange);

    if (endPage - startPage + 1 < maxPagesToShow) {
      if (currentPage - startPage >= halfRange) {
        startPage = Math.max(1, endPage - maxPagesToShow + 1);
      } else {
        endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);
      }
    }

    const range: (number | string)[] = [];

    if (startPage > 1) {
      range.push(1);
      if (startPage > 2) {
        range.push('...');
      }
    }

    for (let i = startPage; i <= endPage; i++) {
      range.push(i);
    }

    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        range.push('...');
      }
      range.push(totalPages);
    }
    return range;
  }, [table]);

  const pages = paginationRange();
  const totalCount = table.getRowCount();
  const firstIndexPerPages = (table.getState().pagination.pageIndex * table.getState().pagination.pageSize) + 1;
  const lastIndexPerPages = Math.min(
    (table.getState().pagination.pageIndex * table.getState().pagination.pageSize) + table.getRowModel().rows.length,
    totalCount
  );


  return (
    <div className={`table-responsive ${className}`} ref={containerRef}>
      {isLoading ? (
        <div className="d-flex align-items-center justify-content-center" style={{ height: '50vh' }}>
          <Spinner animation="border" role="status" variant="primary" className="me-3" />
          <div className="loading-text">
            Loading
            <span className="dot">.</span>
            <span className="dot">.</span>
            <span className="dot">.</span>
          </div>
        </div>
      ) : (
        <>
          <Row className="my-3 mb-6 align-items-center justify-content-between">
            <Col md={6} className="d-flex align-items-center text-muted text-sm">
              <i className="fa-solid fa-circle text-success fs-9 me-2"></i>
              Showing {firstIndexPerPages} to {lastIndexPerPages} of {totalCount} results
            </Col>
            {enableGlobalFilter && (
              <Col md={6} className="d-flex align-items-center justify-content-end mt-2 mt-md-0">
                <Form.Group as={Row} className="mb-0 align-items-center">
                  <Form.Label column className="text-end text-primary me-0">
                    <i className="fa-solid fa-magnifying-glass fa-rotate-90 text-primary fs-9 me-2"></i>Search :
                  </Form.Label>
                  <Col>
                    <Form.Control
                      type="text"
                      size="sm"
                      placeholder={globalFilterPlaceholder}
                      value={globalFilter ?? ''}
                      onChange={(e) => table.setGlobalFilter(String(e.target.value))}
                    />
                  </Col>
                </Form.Group>
              </Col>
            )}
          </Row>

          <Table striped bordered hover className="gy-3" id="data_table">
            <thead className='table-dark'>
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <th
                      key={header.id}
                      className={`py-2 ${ (header.column.columnDef.meta as any)?.headerClass || ''}`}
                    >
                      {header.isPlaceholder ? null : (
                        <div
                          className={
                            enableSorting && header.column.getCanSort()
                              ? 'cursor-pointer select-none d-flex align-items-center'
                              : 'd-flex align-items-center'
                          }
                          onClick={enableSorting ? header.column.getToggleSortingHandler() : undefined}
                          title={
                            enableSorting && header.column.getCanSort()
                              ? header.column.getNextSortingOrder() === 'asc'
                                ? 'Sort ascending'
                                : header.column.getNextSortingOrder() === 'desc'
                                  ? 'Sort descending'
                                  : 'Reset'
                              : undefined
                          }
                        >
                          {flexRender(header.column.columnDef.header, header.getContext())}
                          {enableSorting && header.column.getCanSort() && (
                            <span className="ms-2">
                              {{
                                asc: (<i className="fa-solid fa-sort-up"></i>),
                                desc: (<i className="fa-solid fa-sort-down"></i>),
                                false: (<i className="fa-solid fa-sort"></i>),
                              }[header.column.getIsSorted() as string] ?? null}
                            </span>
                          )}
                        </div>
                      )}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {table.getRowModel().rows.length > 0 ? (
                rows.map((row) => {
                  const rowKey = getRowKey?.(row.original);
                  return (
                  <tr
                    key={row.id}
                    className="align-middle"
                    data-row-key={rowKey}
                    style={onRowClick ? { cursor: 'pointer' } : undefined}
                    onClick={
                      onRowClick
                        ? (event) => {
                          // Let the action column (and any other control in a
                          // cell) do its own thing.
                          if (
                            (event.target as HTMLElement).closest(
                              'a, button, input, select, textarea, label'
                            )
                          ) {
                            return;
                          }
                          onRowClick(
                            row.original,
                            table.getPrePaginationRowModel().rows.map((r) => r.original)
                          );
                        }
                        : undefined
                    }
                  >
                    {row.getVisibleCells().map((cell) => (
                      <td
                        key={cell.id}
                        className={`${(cell.column.columnDef.meta as any)?.cellClass || ''}`}
                        // Striped rows paint their own background over the row,
                        // so the flag has to go on the cells and outrank it.
                        style={
                          rowKey && rowKey === flaggedRowId
                            ? { boxShadow: 'inset 0 0 0 9999px rgba(255, 214, 0, 0.28)' }
                            : undefined
                        }
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={columns.length} className="text-center text-muted py-4">
                    No data available
                  </td>
                </tr>
              )}
            </tbody>
          </Table>

          {enablePagination && (
            <Row className="my-3 align-items-center justify-content-between">
              <Col md={6} className='d-flex align-items-center'>
                Display
                <Form.Select
                  size="sm"
                  className="w-auto mx-2"
                  value={table.getState().pagination.pageSize}
                  onChange={(e) => {
                    table.setPageSize(Number(e.target.value));
                  }}
                >
                  {pageSizes.map((pageSizeOption) => (
                    <option key={pageSizeOption} value={pageSizeOption}>
                      {pageSizeOption}
                    </option>
                  ))}
                </Form.Select>{' '}
                rows per page
              </Col>
              <Col md={6} className='d-flex justify-content-end mt-2 mt-md-0'>
                <Pagination>
                  <Pagination.Prev
                    onClick={() => table.previousPage()}
                    disabled={!table.getCanPreviousPage()}
                  />
                  {pages.map((page, index) =>
                    page === '...' ? (
                      <Pagination.Ellipsis key={index} />
                    ) : (
                      <Pagination.Item
                        key={index}
                        active={table.getState().pagination.pageIndex + 1 === page}
                        onClick={() => table.setPageIndex(Number(page) - 1)}
                      >
                        {page}
                      </Pagination.Item>
                    )
                  )}
                  <Pagination.Next
                    onClick={() => table.nextPage()}
                    disabled={!table.getCanNextPage()}
                  />
                </Pagination>
              </Col>
            </Row>
          )}
        </>
      )}
    </div>
  );
}

export default DataTable;